import { BEIGE_400, GRAY_900 } from 'constants/styling/theme'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import React, { useMemo, useState } from 'react'
import { Stack, Typography } from '@mui/material'

import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { RoomType } from 'constants/visualStaging/visualStagingTypes'
import { RoomTypeSelectorProps } from 'models/visualStaging/virtualStagingTypes'
import { useTranslation } from 'react-i18next'

/**
 * RoomTypeSelector component for Virtual Staging.
 * Matches Figma design with dropdown styling.
 * Allows users to select the type of room for virtual staging generation.
 * 
 * @example
 * <RoomTypeSelector
 *   roomType={virtualStagingState.roomType}
 *   onRoomTypeChange={changeRoomType}
 *   isDisabled={virtualStagingState.isGeneratingStaging}
 *   error={virtualStagingState.roomTypeError}
 * />
 */
export const RoomTypeSelector: React.FC<RoomTypeSelectorProps> = ({
  roomType,
  onRoomTypeChange,
  isDisabled = false,
  error,
}) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const [hasBeenBlurred, setHasBeenBlurred] = useState(false)

  const roomTypeOptions = [
    { value: RoomType.DINING_ROOM, label: t('virtual_staging.room_type.dining_room') },
    { value: RoomType.LIVING_ROOM, label: t('virtual_staging.room_type.living_room') },
    { value: RoomType.BEDROOM, label: t('virtual_staging.room_type.bedroom') },
    { value: RoomType.KID_BEDROOM, label: t('virtual_staging.room_type.kid_bedroom') },
    { value: RoomType.TOILET, label: t('virtual_staging.room_type.toilet') },
    { value: RoomType.BATHROOM, label: t('virtual_staging.room_type.bathroom') },
    { value: RoomType.KITCHEN, label: t('virtual_staging.room_type.kitchen') },
    { value: RoomType.OFFICE, label: t('virtual_staging.room_type.office') },
    { value: RoomType.TERRACE, label: t('virtual_staging.room_type.terrace') },
  ]

  const selectedRoomTypeLabel = roomType
    ? roomTypeOptions.find(option => option.value === roomType)?.label
    : ''

  const handleRoomTypeSelect = (selectedRoomType: RoomType) => {
    onRoomTypeChange(selectedRoomType)
  }

  const handleDropdownClose = () => {
    setHasBeenBlurred(true)
  }

  // Show error only if dropdown has been opened and closed and no room type is selected
  const shouldShowError = useMemo(() => {
    return hasBeenBlurred && !roomType && !!error
  }, [hasBeenBlurred, roomType, error])

  return (
    <Stack gap={0.4}>
      <Typography
        sx={{
          fontSize: '1.6rem',
          fontWeight: 550,
          color: GRAY_900,
          lineHeight: 1.5,
          letterSpacing: '-0.64px',
        }}
      >
        {t('virtual_staging.room_type.title')}*
      </Typography>
      <MUIDropdown
        placement="bottom-start"
        onClose={handleDropdownClose}
        // Portalled and layered above the Photo Studio panels, which clip and scroll their content
        disablePortal={false}
        sx={{
          zIndex: 'modal',
          '& .MuiPaper-root': {
            maxHeight: '18.8rem',
            overflow: 'hidden',
          },
        }}
        button={(_, action) => (
          <MUIDropdownInput
            type={DropdownInputTypes.SELECTOR}
            value={selectedRoomTypeLabel}
            placeholder={t('virtual_staging.room_type.placeholder')}
            disabled={isDisabled}
            isError={!!shouldShowError}
            errorText={shouldShowError ? error : undefined}
            required={true}
            readOnly={true}
            onClick={!isDisabled ? action : undefined}
          />
        )}
      >
        <Stack
          sx={{
            maxHeight: '18.8rem',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: BEIGE_400,
              borderRadius: '4px',
            },
          }}
        >
          {roomTypeOptions.map((option) => (
            <MUIDropdownItem
              key={option.value}
              onClick={() => handleRoomTypeSelect(option.value)}
            >
              {option.label}
            </MUIDropdownItem>
          ))}
        </Stack>
      </MUIDropdown>
    </Stack>
  )
}
